import { onAnimationEnd } from '@theme/utilities';

// Create a new custom element for product links with images for transitions to PDP
class ProductCardLink extends HTMLElement {
  /** @param {Event} event */
  #clickHandler = (event) => {
    setTimeout(() => this.#handleClick(event), 0);
  };

  connectedCallback() {
    this.addEventListener('click', this.#clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#clickHandler);
  }

  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleClick = (event) => {
    // If the event has been prevented, don't do anything, another component is handling the click or if it's an input (swatches)
    if (event.defaultPrevented || event.target instanceof HTMLInputElement) return;

    const gallery = this.querySelector('[data-view-transition-to-main-product]');
    if (!this.productTransitionEnabled || !(gallery instanceof HTMLElement)) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const activeImage =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]') ||
      gallery.querySelector('[transitionToProduct="true"]:last-child');

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    gallery.setAttribute('data-view-transition-type', 'product-image-transition');
    gallery.setAttribute('data-view-transition-triggered', 'true');
  };

  /**
   * Ensures the visible image has a single-URL srcset for the view transition.
   * Uses the image's own URL (data_max_resolution or src) so variant images stay correct
   * and we don't replace them with the first product image.
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    const url = image.getAttribute('data_max_resolution') || image.src || this.featuredMediaUrl;
    if (!url) return;
    if (image.srcset.includes(url)) return;

    const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
      duration: 125,
      easing: 'ease-in-out',
    });

    imageFade.onfinish = () => {
      image.srcset = url;
    };
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}
